import { Alert, styled, Typography } from '@mui/material';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay } from 'further-ui/utils';
import { FC } from 'react';
import CancelCustomRateDialog from './CancelCustomRateDialog';
import CustomFxRateInputDialog from './CustomFxRateInputDialog';

const StyledAlert = styled(Alert)(({ theme }) => ({
  ...theme.typography.caption,
  '&.MuiAlert-standardInfo': {
    backgroundColor: theme.palette.background.infoLight,
  },
}));

const InlineTextButton = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  textDecoration: 'none',
}));

const InlineRedTextButton = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.error.main,
  cursor: 'pointer',
  textDecoration: 'none',
}));

type Props = {
  currency: CurrencyCode;
  date?: CalendarDay;
  isFetchingRate: boolean;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  fxRate: CurrencyRateDetails | null;
  onRateChange?: (rateDetails: CurrencyRateDetails) => void;
  readOnly?: boolean;
  forceShow?: boolean;
};

const CurrencyRate: FC<Props> = ({
  currency,
  date,
  vehicle,
  fxRate,
  onRateChange,
  readOnly = false,
  isFetchingRate,
  forceShow = false,
}) => {
  const { baseCurrency, fxDataFeed } = vehicle;
  const customRateInputDisclosure = useDisclosure();
  const cancelCustomRateDisclosure = useDisclosure();

  if (currency === baseCurrency && !forceShow) {
    return null;
  }

  if (isFetchingRate)
    return <Typography variant="caption">Loading exchange rate...</Typography>;

  const isUnknownFutureRate =
    !fxRate && date?.isAfter(new CalendarDay(new Date()));

  if (isUnknownFutureRate) {
    return (
      <StyledAlert severity="info" icon={false}>
        As this is a future transaction, the FX rate will be available for
        review at the earlier of: (i) the point of payment; or (ii) the first
        end-of-period sign off process following the action date.
      </StyledAlert>
    );
  }

  const fxRateValue = fxRate?.overrideRate
    ? fxRate.overrideRate
    : fxRate?.fetchedRate;
  const fxRateSource = fxRate?.overrideRate
    ? fxRate.overrideSource
    : fxDataFeed;
  const rateText = `${baseCurrency} 1.00 = ${currency} ${fxRateValue ?? '--'}${
    date ? ` on ${date.toReadable()}` : ''
  } (Source: ${fxRateSource})`;

  return (
    <>
      {!readOnly && !!onRateChange && (
        <>
          <CustomFxRateInputDialog
            disclosure={customRateInputDisclosure}
            baseCurrency={baseCurrency}
            transactionCurrency={currency}
            onRateChange={onRateChange}
            fxRate={fxRate}
          />
          <CancelCustomRateDialog
            disclosure={cancelCustomRateDisclosure}
            onRateChange={onRateChange}
            fxRate={fxRate}
          />
        </>
      )}

      {fxRate?.overrideRate ? (
        <Row centered spacing="xxs">
          <Typography variant="caption">{rateText}</Typography>
          {!readOnly && (
            <>
              <InlineTextButton onClick={customRateInputDisclosure.onOpen}>
                Amend manual rate
              </InlineTextButton>
              <Typography variant="caption">/</Typography>
              <InlineRedTextButton onClick={cancelCustomRateDisclosure.onOpen}>
                Revert to live FX feed
              </InlineRedTextButton>
            </>
          )}
        </Row>
      ) : (
        <Row centered spacing="xxs">
          <Typography variant="caption">{rateText}</Typography>

          {!readOnly && (
            <InlineTextButton onClick={customRateInputDisclosure.onOpen}>
              Override rate
            </InlineTextButton>
          )}
        </Row>
      )}
    </>
  );
};

export default CurrencyRate;
